/**
 * Aba Resumo do detalhe: retorno da empresa no periodo COMPARADO com o
 * anterior, a saude da conta, o ritmo com a projecao e os funis.
 *
 * Decisoes nao obvias:
 *  - O que a administracao ve aqui e o MESMO retorno que o lojista ve na
 *    visao geral (recuperado, comissao, liquido, taxa, ticket): a equipe
 *    precisa enxergar o painel do cliente, nao um painel paralelo. O cartao
 *    em destaque e um so (o recuperado); os demais sao apoio.
 *  - Toda medida vem com a variacao contra o periodo anterior de mesmo
 *    tamanho (glifo + texto). Sem base, escreve "sem base anterior".
 *  - "Se a taxa voltasse ao patamar anterior" transforma a queda de taxa em
 *    dinheiro: vendas a mais = abandonos x diferenca de taxa, ao ticket medio.
 *    Tudo em inteiros; e o argumento que a equipe leva para a empresa.
 *  - Funis so DEPOIS do fato: cada cartao diz o fato que dispara a regua.
 *    Funil desligado com oferta recorrente vira oportunidade escrita, nunca
 *    "lembrete antes de vencer".
 */

import { KpiCard } from '../../../components/KpiCard';
import { Badge, BarChart, Icon, Panel, SectionCard } from '../../../components/ui';
import { VALOR_AUSENTE, formatBRLFromCents, formatCount, formatPercentFromBps } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import { diasEntre, projetar } from '../comparar';
import type { AbaDetalhe, CompanyDetail, SaudeDaEmpresa } from '../types';
import { FUNIL_INFO, FUNIS } from '../vocabulario';
import { Delta } from './Delta';
import { CartaoDeSaude } from './SaudeEmpresa';

function RitmoEProjecao({ detail, className = '' }: { readonly detail: CompanyDetail; readonly className?: string }): JSX.Element {
  const m = detail.metrics;
  const p = detail.previous;
  const dias = diasEntre(detail.periodStart, detail.periodEnd);
  const porDia = projetar(m.recoveredAmountCents, dias, 1);
  const em30 = projetar(m.recoveredAmountCents, dias, 30);
  const comissao30 = projetar(m.commissionCents, dias, 30);
  const quedaTaxa = p !== null && p.abandonedCount > 0 && m.abandonedCount > 0 ? p.recoveryRateBps - m.recoveryRateBps : 0;
  const vendasAMais = quedaTaxa > 0 ? Math.round((m.abandonedCount * quedaTaxa) / 10_000) : 0;
  const ticket = m.averageTicketCents > 0 ? m.averageTicketCents : p?.averageTicketCents ?? 0;
  const dinheiroAMais = vendasAMais * ticket;

  return (
    <SectionCard id="resumo-ritmo" title="Ritmo e projeção" icon="grafico" description={`No ritmo dos ${formatCount(dias)} dias da janela. Linear: é o ritmo atual em outra unidade, não uma previsão.`} className={className}>
      <dl className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-line bg-elevated px-4 py-3">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Recuperado por dia</dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums text-ink">{porDia === null ? VALOR_AUSENTE : formatBRLFromCents(porDia)}</dd>
        </div>
        <div className="rounded-xl border border-line bg-elevated px-4 py-3">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">30 dias neste ritmo</dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums text-ink">{em30 === null ? VALOR_AUSENTE : formatBRLFromCents(em30)}</dd>
        </div>
        <div className="rounded-xl border border-line bg-elevated px-4 py-3">
          <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Comissão em 30 dias</dt>
          <dd className="mt-1 text-lg font-semibold tabular-nums text-ink">{comissao30 === null ? VALOR_AUSENTE : formatBRLFromCents(comissao30)}</dd>
        </div>
      </dl>
      {vendasAMais > 0 && p !== null ? (
        <p className="mt-3 flex items-start gap-2 rounded-lg border border-status-warning/50 bg-status-warning/5 px-3 py-2 text-sm text-ink">
          <Icon name="alerta" size="sm" className="mt-0.5 shrink-0 text-status-warning" />
          <span>
            Se a taxa voltasse aos <strong>{formatPercentFromBps(p.recoveryRateBps)}</strong> do período anterior, seriam cerca de <strong>{formatCount(vendasAMais)}</strong>{' '}
            {vendasAMais === 1 ? 'venda' : 'vendas'} a mais, <strong>{formatBRLFromCents(dinheiroAMais)}</strong> recuperados ao ticket médio. A saúde ao lado diz o que provavelmente
            derrubou a taxa.
          </span>
        </p>
      ) : (
        <p className="mt-3 text-xs text-ink-subtle">
          Sem a régua, {formatBRLFromCents(m.recoveredAmountCents)} em vendas teriam ficado para trás neste período; {formatBRLFromCents(m.netAmountCents)} ficaram com a empresa.
        </p>
      )}
    </SectionCard>
  );
}

export function AbaResumo({
  detail,
  company,
  saude,
  onIrPara,
}: {
  readonly detail: CompanyDetail;
  readonly company: CompanySummary;
  readonly saude: SaudeDaEmpresa;
  readonly onIrPara: (aba: AbaDetalhe) => void;
}): JSX.Element {
  const m = detail.metrics;
  const p = detail.previous;
  const semVenda = m.recoveredCount === 0;
  const labels = detail.weekly.map((w) => w.label);
  // Comissao ja apurada fica CONGELADA no percentual da epoca (ADR-001). Se a
  // administracao acabou de mudar o percentual, "20% sobre o recuperado" em
  // cima de um valor apurado a 15% seria uma conta que nao fecha na tela.
  const percentuaisAplicados = [...new Set(detail.commissionHistory.map((mes) => mes.feeBps))].sort((a, b) => a - b);
  const percentualBate = percentuaisAplicados.every((bps) => bps === company.feeBps);
  const apoioComissao = percentualBate
    ? `${formatPercentFromBps(company.feeBps)} sobre o valor recuperado`
    : `Apurada a ${percentuaisAplicados.map((bps) => formatPercentFromBps(bps)).join(' e ')}, congelada por venda; ${formatPercentFromBps(company.feeBps)} vale para as próximas`;

  return (
    <div className="flex flex-col gap-6">
      <KpiCard
        emphasis
        label="Recuperado no período"
        value={formatBRLFromCents(m.recoveredAmountCents)}
        support={
          semVenda
            ? 'Nenhuma venda recuperada na janela.'
            : `${formatCount(m.recoveredCount)} ${m.recoveredCount === 1 ? 'venda recuperada' : 'vendas recuperadas'} · ticket médio ${formatBRLFromCents(m.averageTicketCents)}`
        }
        icon={<Icon name="moeda" size="sm" />}
        aside={
          <div className="flex flex-col items-end gap-1 text-right">
            <Delta atual={m.recoveredAmountCents} anterior={p === null ? null : p.recoveredAmountCents} tipo="dinheiro" monocromatico className="text-sm" />
            <span className="text-xs text-white/75">{p === null ? 'a conta não existia no período anterior' : `antes: ${formatBRLFromCents(p.recoveredAmountCents)}`}</span>
          </div>
        }
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard
          label="Comissão Converte Fácil"
          value={formatBRLFromCents(m.commissionCents)}
          support={apoioComissao}
          icon={<Icon name="comissao" size="sm" />}
          aside={<Delta atual={m.commissionCents} anterior={p === null ? null : p.commissionCents} tipo="dinheiro" compacto />}
        />
        <KpiCard
          label="Líquido para a empresa"
          value={formatBRLFromCents(m.netAmountCents)}
          support="Recuperado menos a comissão"
          icon={<Icon name="empresa" size="sm" />}
          aside={<Delta atual={m.netAmountCents} anterior={p === null ? null : p.netAmountCents} tipo="dinheiro" compacto />}
        />
        <KpiCard
          label="Taxa de recuperação"
          value={m.abandonedCount === 0 ? VALOR_AUSENTE : formatPercentFromBps(m.recoveryRateBps)}
          support={`${formatCount(m.recoveredCount)} de ${formatCount(m.abandonedCount)} checkouts abandonados`}
          icon={<Icon name="funil" size="sm" />}
          aside={m.abandonedCount === 0 ? undefined : <Delta atual={m.recoveryRateBps} anterior={p === null || p.abandonedCount === 0 ? null : p.recoveryRateBps} tipo="bps" compacto />}
        />
        <KpiCard
          label="Ticket médio"
          value={semVenda ? VALOR_AUSENTE : formatBRLFromCents(m.averageTicketCents)}
          support="Por venda recuperada"
          icon={<Icon name="carrinho" size="sm" />}
          aside={semVenda ? undefined : <Delta atual={m.averageTicketCents} anterior={p === null || p.recoveredCount === 0 ? null : p.averageTicketCents} tipo="dinheiro" compacto />}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <CartaoDeSaude saude={saude} onIrPara={onIrPara} className="lg:row-span-2" />

        <SectionCard
          id="resumo-semanal"
          title="Recuperado x comissão por semana"
          icon="grafico"
          description="Cada barra é uma semana da janela do cabeçalho. A comissão é a fatia do Converte Fácil sobre o recuperado."
          className="lg:col-span-2"
        >
          {labels.length === 0 ? (
            <p className="py-8 text-center text-sm text-ink-muted">A janela não cobre nenhum dia depois da criação da empresa. Escolha um período maior no cabeçalho.</p>
          ) : (
            <BarChart
              title={`Recuperado e comissão por semana de ${company.name}`}
              labels={labels}
              series={[
                { key: 'recuperado', label: 'Recuperado', values: detail.weekly.map((w) => w.recoveredAmountCents), tone: 1 },
                { key: 'comissao', label: 'Comissão', values: detail.weekly.map((w) => w.commissionCents), tone: 2 },
              ]}
              formatValue={formatBRLFromCents}
              height={220}
            />
          )}
        </SectionCard>

        <RitmoEProjecao detail={detail} className="lg:col-span-2" />
      </div>

      <SectionCard id="resumo-funis" title="Funis" icon="funil" description="Réguas desta empresa. Todas começam depois do fato: nada de lembrete antes de vencer.">
        <ul className="grid gap-4 md:grid-cols-2">
          {FUNIS.map((kind) => {
            const info = FUNIL_INFO[kind];
            const ativo = company.funnels.includes(kind);
            const oportunidade = !ativo && kind === 'expirados' && detail.setup.recurringOffers > 0;
            return (
              <li key={kind}>
                <Panel tone={ativo ? 'surface' : 'elevated'} className={`flex h-full flex-col gap-3 p-4 ${ativo ? '' : 'border-dashed border-edge'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <h3 className={`flex items-center gap-2 text-sm font-semibold ${ativo ? 'text-ink' : 'text-ink-muted'}`}>
                      <Icon name={info.icon} size="sm" />
                      {info.label}
                    </h3>
                    <Badge tone={ativo ? 'good' : 'neutral'} dot>
                      {ativo ? 'Ativo' : 'Não ativado'}
                    </Badge>
                  </div>
                  <p className="text-xs text-ink-muted">{info.descricao}</p>
                  <p>
                    <Badge tone={ativo ? 'brand' : 'neutral'} icon="escudo">
                      Dispara em: {info.fato}
                    </Badge>
                  </p>
                  {ativo ? (
                    <ol className="flex flex-col gap-1 text-xs text-ink-muted">
                      {info.regua.map((linha, i) => (
                        <li key={linha} className="flex items-start gap-2">
                          <span aria-hidden="true" className="mt-px grid h-4 w-4 shrink-0 place-items-center rounded-full bg-brand/10 text-[9px] font-semibold text-brand">
                            {i + 1}
                          </span>
                          {linha}
                        </li>
                      ))}
                    </ol>
                  ) : null}
                  {oportunidade ? (
                    <p className="mt-auto flex items-start gap-2 rounded-lg border border-info/30 bg-info/5 px-3 py-2 text-xs text-ink">
                      <Icon name="info" size="xs" className="mt-0.5 shrink-0 text-info" />
                      <span>
                        {formatCount(detail.setup.recurringOffers)} {detail.setup.recurringOffers === 1 ? 'oferta recorrente' : 'ofertas recorrentes'} no catálogo: assinaturas que já
                        venceram estão sem régua e sem comissão. Ligar este funil é conversa com a empresa, não configuração daqui.
                      </span>
                    </p>
                  ) : null}
                </Panel>
              </li>
            );
          })}
        </ul>
      </SectionCard>
    </div>
  );
}
